import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AjudaTooltip from '../components/AjudaTooltip';
import { temPermissao } from '../utils/permissoes';
import ModalConfirmacao from '../components/ModalConfirmacao';

const FORM_VAZIO = {
  nome_cliente: '',
  telefones: [{ tipo: 'celular', numero: '', principal: true }],
  email_cliente: '',
  endereco_cliente: '',
  tipo_cliente: '',
  segmento: '',
};

function formatarTelefone(valor, tipo) {
  const n = valor.replace(/\D/g, '');
  const isCelular = tipo === 'celular';
  const max = isCelular ? 11 : 10;
  const d = n.slice(0, max);
  if (d.length <= 2) return `(${d}`;
  if (isCelular) {
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  } else {
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  }
}

function TelefonesEditor({ telefones, onChange, compact }) {
  function handleNumero(i, valor) {
    const novos = telefones.map((t, idx) =>
      idx === i ? { ...t, numero: formatarTelefone(valor, t.tipo) } : t
    );
    onChange(novos);
  }

  function handleTipo(i, novoTipo) {
    const novos = telefones.map((t, idx) =>
      idx === i ? { ...t, tipo: novoTipo, numero: '' } : t
    );
    onChange(novos);
  }

  function handlePrincipal(i) {
    const novos = telefones.map((t, idx) => ({ ...t, principal: idx === i }));
    onChange(novos);
  }

  function adicionar() {
    onChange([...telefones, { tipo: 'celular', numero: '', principal: telefones.length === 0 }]);
  }

  function remover(i) {
    let novos = telefones.filter((_, idx) => idx !== i);
    if (telefones[i].principal && novos.length > 0) {
      novos[0] = { ...novos[0], principal: true };
    }
    onChange(novos);
  }

  const inputStyle = compact ? { marginBottom: 0, fontSize: 12 } : { marginBottom: 0 };
  const selectStyle = compact
    ? { width: 95, marginBottom: 0, fontSize: 12 }
    : { width: 110, marginBottom: 0 };

  return (
    <div>
      {telefones.map((t, i) => (
        <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 6, alignItems: 'center' }}>
          <select value={t.tipo} onChange={e => handleTipo(i, e.target.value)} style={selectStyle}>
            <option value="celular">Celular</option>
            <option value="comercial">Comercial</option>
            <option value="fixo">Fixo</option>
          </select>
          <input
            value={t.numero}
            onChange={e => handleNumero(i, e.target.value)}
            placeholder={{ celular: 'Telefone Celular', comercial: 'Telefone Comercial', fixo: 'Telefone Fixo' }[t.tipo]}
            style={{ ...inputStyle, flex: 1, minWidth: compact ? 110 : 140 }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap', color: 'var(--text-primary)', flexShrink: 0 }}>
            <input
              type="checkbox"
              checked={t.principal}
              onChange={() => handlePrincipal(i)}
              style={{ width: 14, height: 14, cursor: 'pointer', margin: 0 }}
            />
            Principal
          </label>
          <button
            type="button"
            onClick={() => remover(i)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 18, lineHeight: '1', padding: '0 4px', flexShrink: 0, display: 'flex', alignItems: 'center' }}
          >×</button>
        </div>
      ))}
      <button
        type="button"
        onClick={adicionar}
        style={{ fontSize: 12, color: 'var(--azul-medio)', background: 'none', border: '1px dashed var(--azul-medio)', borderRadius: 6, cursor: 'pointer', padding: '3px 10px', marginTop: 2 }}
      >+ Telefone</button>
    </div>
  );
}

function telefonePrincipal(telefones) {
  if (!telefones || telefones.length === 0) return null;
  return telefones.find(t => t.principal) || telefones[0];
}

function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [form, setForm] = useState(FORM_VAZIO);
  const [editando, setEditando] = useState(null);
  const [formEdicao, setFormEdicao] = useState({});
  const [filtroColuna, setFiltroColuna] = useState(() => localStorage.getItem('contatos_filtroColuna') || 'nome_cliente');
  const [filtroInput, setFiltroInput] = useState('');
  const [filtroAplicado, setFiltroAplicado] = useState(() => {
    const col = localStorage.getItem('contatos_filtroColuna') || 'nome_cliente';
    return col === 'tipo_cliente' ? (localStorage.getItem('contatos_filtroValor') || '') : '';
  });
  const [confirmacao, setConfirmacao] = useState({ aberto: false, id: null, nome: '' });

  function pesquisar() { setFiltroAplicado(filtroInput); }

  async function carregarClientes() {
    const response = await api.get('/clientes');
    setClientes(response.data);
  }

  useEffect(() => { carregarClientes(); }, []);

  function formatarNome(valor) {
    return valor.toLowerCase().split(' ')
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  function handleChangeEdicao(e) {
    const { name, value } = e.target;
    setFormEdicao(f => ({ ...f, [name]: value }));
  }

  function validarTelefones(telefones) {
    for (const t of telefones) {
      const d = t.numero.replace(/\D/g, '');
      if (t.tipo === 'celular' && d.length < 11) {
        toast.error('Celular inválido! Use o formato (44) 99999-9999'); return false;
      }
      if ((t.tipo === 'comercial' || t.tipo === 'fixo') && d.length < 10) {
        toast.error(`Telefone ${t.tipo} inválido! Use o formato (44) 3030-3030`); return false;
      }
    }
    return true;
  }

  function validarForm(f) {
    if (!f.nome_cliente.trim()) { toast.error('Nome é obrigatório!'); return false; }
    if (!f.tipo_cliente) { toast.error('Tipo é obrigatório!'); return false; }
    if (f.tipo_cliente === 'fornecedor' && !f.segmento?.trim()) {
      toast.error('Segmento é obrigatório para fornecedores!'); return false;
    }
    if (f.email_cliente && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email_cliente)) {
      toast.error('Email inválido! Ex: nome@dominio.com'); return false;
    }
    if (!validarTelefones(f.telefones || [])) return false;
    return true;
  }

  async function criarCliente() {
    if (!validarForm(form)) return;
    try {
      await api.post('/clientes', { ...form, nome_cliente: formatarNome(form.nome_cliente) });
      setForm(FORM_VAZIO);
      // Limpa qualquer filtro aplicado para o novo contato aparecer na lista
      setFiltroInput('');
      setFiltroAplicado('');
      if (filtroColuna === 'tipo_cliente') localStorage.removeItem('contatos_filtroValor');
      carregarClientes();
      toast.success('Contato cadastrado com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao cadastrar contato.');
    }
  }

  function iniciarEdicao(c) {
    setEditando(c.clienteid);
    setFormEdicao({ ...c, telefones: JSON.parse(JSON.stringify(c.telefones || [])) });
  }

  async function salvarEdicao() {
    if (!validarForm(formEdicao)) return;
    try {
      await api.put(`/clientes/${editando}`, {
        ...formEdicao,
        nome_cliente: formatarNome(formEdicao.nome_cliente),
      });
      setEditando(null);
      carregarClientes();
      toast.success('Cliente atualizado com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao atualizar contato.');
    }
  }

  async function deletarCliente(id) {
    try {
      await api.delete(`/clientes/${id}`);
      carregarClientes();
      toast.success('Cliente removido com sucesso!');
    } catch (err) {
      const msg = err.response?.data?.mensagem;
      if (err.response?.status === 403) {
        toast.error(msg || 'Sem permissão para excluir clientes.', { duration: 5000 });
      } else {
        toast.error('Este cliente não pode ser excluído pois possui obras vinculadas.', { duration: 5000 });
      }
    }
  }

  async function confirmarDeletar() {
    await deletarCliente(confirmacao.id);
    setConfirmacao({ aberto: false, id: null, nome: '' });
  }

  const TIPOS = [
    { value: 'cliente',     label: 'Cliente',     bg: '#d1e7dd', color: '#0f5132' },
    { value: 'construtor',  label: 'Construtor',  bg: '#fef3c7', color: '#92400e' },
    { value: 'executor',    label: 'Executor',    bg: '#dbeafe', color: '#1e40af' },
    { value: 'fornecedor',  label: 'Fornecedor',  bg: '#fff1f2', color: '#be123c' },
    { value: 'terceiros',   label: 'Terceiros',   bg: '#f3e8ff', color: '#6b21a8' },
  ];

  function renderTipo(tipo) {
    const config = TIPOS.find(t => t.value === tipo) || { label: tipo || '—', bg: '#e0e0e0', color: '#333' };
    return (
      <span style={{ background: config.bg, color: config.color, padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
        {config.label}
      </span>
    );
  }

  const clientesFiltrados = clientes.filter(c => {
    if (filtroColuna === 'telefones') {
      return (c.telefones || []).some(t =>
        t.numero.toLowerCase().includes(filtroAplicado.toLowerCase())
      );
    }
    return String(c[filtroColuna] || '').toLowerCase().includes(filtroAplicado.toLowerCase());
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}>👥 Contatos</h2>
        <AjudaTooltip texto={`Cadastre aqui todos os seus contatos.\n\n• Cliente: quem contratou a obra.\n• Executor: profissional que executa serviços.\n• Fornecedor: empresa ou pessoa que fornece materiais. Ao selecionar este tipo, informe também o Segmento (ex: Materiais de Construção, Elétrica...). Fornecedores ficam disponíveis para seleção na aba de materiais das obras em andamento.\n• Construtor / Terceiros: demais envolvidos.`} />
      </div>

      <div className="card">
        <h3>Novo Cadastro</h3>
        <input name="nome_cliente" placeholder="Nome *" value={form.nome_cliente} onChange={handleChange} />
        <select name="tipo_cliente" value={form.tipo_cliente} onChange={handleChange} required>
          <option value="">Tipo *</option>
          {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {form.tipo_cliente === 'fornecedor' && (
          <input name="segmento" placeholder="Segmento * (ex: Elétrica, Hidráulica, Materiais...)" value={form.segmento} onChange={handleChange} />
        )}
        <input name="email_cliente" placeholder="Email" value={form.email_cliente} onChange={handleChange} />
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Telefones</label>
          <TelefonesEditor
            telefones={form.telefones}
            onChange={telefones => setForm(f => ({ ...f, telefones }))}
          />
        </div>
        <input name="endereco_cliente" placeholder="Endereço" value={form.endereco_cliente} onChange={handleChange} />
        <button className="btn-primary" onClick={criarCliente}>Adicionar</button>
      </div>

      <div className="card">
        <h3>Contatos Cadastrados</h3>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <select value={filtroColuna} onChange={e => { const v = e.target.value; setFiltroColuna(v); setFiltroInput(''); setFiltroAplicado(''); localStorage.setItem('contatos_filtroColuna', v); localStorage.removeItem('contatos_filtroValor'); }} style={{ minWidth: 140, marginBottom: 0 }}>
            <option value="clienteid">Cód. Cliente</option>
            <option value="nome_cliente">Nome</option>
            <option value="tipo_cliente">Tipo</option>
            <option value="telefones">Telefone</option>
            <option value="email_cliente">Email</option>
            <option value="endereco_cliente">Endereço</option>
          </select>
          {filtroColuna === 'tipo_cliente' ? (
            <select value={filtroAplicado} onChange={e => { const v = e.target.value; setFiltroInput(v); setFiltroAplicado(v); localStorage.setItem('contatos_filtroValor', v); }} style={{ flex: 1, marginBottom: 0 }}>
              <option value="">Todos</option>
              {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          ) : (
            <input placeholder="Pesquisar..." value={filtroInput} onChange={e => setFiltroInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && pesquisar()} style={{ flex: 1, marginBottom: 0 }} />
          )}
          {filtroColuna !== 'tipo_cliente' && (
            <button className="btn-primary" onClick={pesquisar} style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Pesquisar</button>
          )}
        </div>

        <table>
          <thead>
            <tr>
              <th>Cód. Cliente</th>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Segmento</th>
              <th>Telefone</th>
              <th>Email</th>
              <th>Endereço</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {clientesFiltrados.map((c) => (
              editando === c.clienteid ? (
                <tr key={c.clienteid} style={{ background: 'var(--bg-secondary)' }}>
                  <td data-label="Cód. Cliente">{c.clienteid}</td>
                  <td data-label="Nome"><input name="nome_cliente" value={formEdicao.nome_cliente} onChange={handleChangeEdicao} style={{ marginBottom: 0 }} /></td>
                  <td data-label="Tipo">
                    <select name="tipo_cliente" value={formEdicao.tipo_cliente || ''} onChange={handleChangeEdicao} style={{ marginBottom: 0 }}>
                      <option value="">Tipo *</option>
                      {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </td>
                  <td data-label="Segmento">
                    {formEdicao.tipo_cliente === 'fornecedor' ? (
                      <input name="segmento" value={formEdicao.segmento || ''} onChange={handleChangeEdicao} placeholder="Segmento *" style={{ marginBottom: 0 }} />
                    ) : '—'}
                  </td>
                  <td data-label="Telefone">
                    <TelefonesEditor
                      telefones={formEdicao.telefones || []}
                      onChange={telefones => setFormEdicao(f => ({ ...f, telefones }))}
                      compact
                    />
                  </td>
                  <td data-label="Email"><input name="email_cliente" value={formEdicao.email_cliente || ''} onChange={handleChangeEdicao} style={{ marginBottom: 0 }} /></td>
                  <td data-label="Endereço"><input name="endereco_cliente" value={formEdicao.endereco_cliente || ''} onChange={handleChangeEdicao} style={{ marginBottom: 0 }} /></td>
                  <td data-label="Ações">
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-primary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={salvarEdicao}>Salvar</button>
                      <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => setEditando(null)}>Cancelar</button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={c.clienteid}>
                  <td data-label="Cód. Cliente">{c.clienteid}</td>
                  <td data-label="Nome">{c.nome_cliente}</td>
                  <td data-label="Tipo">{renderTipo(c.tipo_cliente)}</td>
                  <td data-label="Segmento">{c.segmento || '—'}</td>
                  <td data-label="Telefone">
                    {(() => {
                      const tel = telefonePrincipal(c.telefones);
                      if (!tel) return '—';
                      const label = { celular: '📱', comercial: '🏢', fixo: '☎️' }[tel.tipo] || '📞';
                      return (
                        <div>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label} </span>
                          {tel.numero}
                          {c.telefones.length > 1 && (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>
                              +{c.telefones.length - 1}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td data-label="Email">{c.email_cliente || '—'}</td>
                  <td data-label="Endereço">{c.endereco_cliente || '—'}</td>
                  <td data-label="Ações">
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => iniciarEdicao(c)}>Editar</button>
                      {temPermissao('clientes_excluir') && (
                        <button className="btn-danger" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => setConfirmacao({ aberto: true, id: c.clienteid, nome: c.nome_cliente })}>Deletar</button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            ))}
            {clientesFiltrados.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>Nenhum contato encontrado</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ModalConfirmacao
        aberto={confirmacao.aberto}
        titulo="Excluir contato"
        mensagem={`Tem certeza que deseja excluir o contato "${confirmacao.nome}"? Esta ação não pode ser desfeita.`}
        onConfirmar={confirmarDeletar}
        onCancelar={() => setConfirmacao({ aberto: false, id: null, nome: '' })}
      />
    </div>
  );
}

export default Clientes;
